import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {BorderType} from '@PosterWhiteboard/classes/item-border.class';
import {BaseBorderThicknessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-border-thickness-option';
import {BaseBorderColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-border-color-option';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import {ContextualMenu} from '@Components/contextual-menu';
import {getAssetUrl} from '@Utils/s3.util';
import {getCompatibleVideoFileExtension} from '@Utils/video.util';
import styles from './border-effect-options.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromBorderOfActiveItem} from '@Hooks/poster-editor/useGetKeyFromBorderOfActiveItem';

import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';

interface BorderEffectOptionsProps {
  panelId: string;

  onClickColorFill(): void;
}

const THUMBNAIL_BASE_URL = 'poster-editor-thumbnails/border/';

export function BorderEffectOptions({...props}: BorderEffectOptionsProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeItemBorderType = useGetKeyFromBorderOfActiveItem('solidBorderType');
  const activeItemBorderColor = useGetKeyFromBorderOfActiveItem('solidBorderColor');
  const activeItemBorderThickness = useGetKeyFromBorderOfActiveItem('solidBorderThickness');
  const hasBorder = activeItem?.border.hasBorder();

  useEffect((): void => {
    if (activeItem && 'border' in activeItem) {
      updateHeight(activeItem.border.hasBorder());
    }
  }, [hasBorder]);

  if (!activeItem || (activeItem && !('border' in activeItem)) || activeItemBorderThickness === undefined || !activeItemBorderColor || activeItemBorderType === undefined) {
    return null;
  }

  const updateHeight = (border: boolean): void => {
    let height = '182px';

    if (border) {
      height = '310px';
    }

    updateOptionsPanelHeight(props.panelId, height);
  };

  const applyBorder = (type: BorderType): void => {
    if (activeItemBorderType === type) {
      return;
    }
    void activeItem.updateFromObject({
      border: {
        solidBorderType: type,
      },
    });
    updateHeight(type !== BorderType.NONE);
  };

  const getOptions = (): ReactElement => {
    return (
      <ul className="flex-h-row spacing-m-0">
        <ContextualMenuThumbnailItem
          className={styles.thumbnailsClasses}
          thumbnailContainerClassName={styles.largeThumbnail}
          thumbnail={
            <Thumbnail
              className={styles.thumbnail}
              data={{
                type: ThumbnailType.IMAGE,
                imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}none.${getCompatibleVideoFileExtension('webp')}`),
              }}
            />
          }
          isSelected={activeItemBorderType === BorderType.NONE}
          selectionState={ThumbnailItemSelectionState.OUTLINE}
          text={window.i18next.t('pmwjs_none')}
          hasBorder={false}
          onClick={(): void => {
            applyBorder(BorderType.NONE);
          }}
        />
        <ContextualMenuThumbnailItem
          className={styles.thumbnailsClasses}
          thumbnailContainerClassName={styles.largeThumbnail}
          thumbnail={
            <Thumbnail
              className={styles.thumbnail}
              data={{
                type: ThumbnailType.IMAGE,
                imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}rectangle.${getCompatibleVideoFileExtension('webp')}`),
              }}
            />
          }
          isSelected={activeItemBorderType === BorderType.RECTANGLE_BORDER}
          selectionState={ThumbnailItemSelectionState.OUTLINE}
          text={window.i18next.t('pmwjs_rectangle')}
          hasBorder={false}
          onClick={(): void => {
            applyBorder(BorderType.RECTANGLE_BORDER);
          }}
        />
        <ContextualMenuThumbnailItem
          className={styles.thumbnailsClasses}
          thumbnailContainerClassName={styles.largeThumbnail}
          thumbnail={
            <Thumbnail
              className={styles.thumbnail}
              data={{
                type: ThumbnailType.IMAGE,
                imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}rounded.${getCompatibleVideoFileExtension('webp')}`),
              }}
            />
          }
          isSelected={activeItemBorderType === BorderType.ROUNDED_BORDER}
          selectionState={ThumbnailItemSelectionState.OUTLINE}
          text={window.i18next.t('pmwjs_rounded')}
          hasBorder={false}
          onClick={(): void => {
            applyBorder(BorderType.ROUNDED_BORDER);
          }}
        />
        {activeItem.isImage() && (activeItem.isItemTransparent() || activeItem.isIcon()) ? (
          <ContextualMenuThumbnailItem
            className={styles.thumbnailsClasses}
            thumbnailContainerClassName={styles.largeThumbnail}
            thumbnail={
              <Thumbnail
                className={styles.thumbnail}
                data={{
                  type: ThumbnailType.IMAGE,
                  imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}outline.${getCompatibleVideoFileExtension('webp')}`),
                }}
              />
            }
            isSelected={activeItemBorderType === BorderType.STROKE_BORDER}
            selectionState={ThumbnailItemSelectionState.OUTLINE}
            text={window.i18next.t('pmwjs_outline')}
            hasBorder={false}
            onClick={(): void => {
              applyBorder(BorderType.STROKE_BORDER);
            }}
          />
        ) : null}
      </ul>
    );
  };

  const updateThickness = (solidBorderThickness: number, undoable = true): void => {
    void activeItem.updateFromObject(
      {
        border: {
          solidBorderThickness,
        },
      },
      {
        undoable,
      }
    );
  };

  const isForLayout = (): boolean => {
    return activeItem.isMenu() || activeItem.isTableItem() || activeItem.isTab();
  };

  const getSubOptions = (): ReactElement | null => {
    if (!activeItem.border.hasBorder()) {
      return null;
    }
    return (
      <>
        <BaseBorderColorOption
          solidBorderColor={activeItemBorderColor}
          onClickColorFill={(): void => {
            props.onClickColorFill();
          }}
          className="spacing-m-b-4"
        />
        <BaseBorderThicknessOption
          className="spacing-m-b-4"
          isForLayout={isForLayout()}
          solidBorderThickness={activeItemBorderThickness}
          updateThickness={updateThickness}
          borderType={activeItemBorderType}
        />
      </>
    );
  };

  return (
    <ContextualMenu id="border-effect-mobile-panel" hasMobileModalPadding hasMobileMorphingProperties>
      {getSubOptions()}
      {getOptions()}
    </ContextualMenu>
  );
}
